import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Store,
  Package,
  AlertCircle,
  RefreshCw,
  BarChart3,
  Clock,
  Users,
  AlertTriangle,
} from "lucide-react";
import { Link } from "wouter";
import { getOwnerRealtimeStats, getOwnerDiscrepancies } from "@/lib/api";
import { getCurrentOwner } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";
import { formatCurrency, getCurrencyName } from "@/lib/currency";

export default function OwnerDashboardKPI() {
  const owner = getCurrentOwner();
  const [lastUpdate, setLastUpdate] = useState<string>(
    new Date().toISOString(),
  );

  // Consulta automática cada 60 segundos
  const {
    data: realtimeData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["owner-realtime-stats", owner?.id],
    queryFn: () => getOwnerRealtimeStats(owner!.id),
    enabled: !!owner,
    refetchInterval: 60000, // 60 segundos
    refetchIntervalInBackground: true,
  });

  // Consulta para discrepancias de caja
  const { data: discrepanciesData, isLoading: discrepanciesLoading } = useQuery(
    {
      queryKey: ["owner-discrepancies", owner?.id],
      queryFn: () => getOwnerDiscrepancies(owner!.id),
      enabled: !!owner,
    },
  );

  const discrepancies = discrepanciesData?.discrepancies || [];

  // Actualizar timestamp cuando los datos cambian
  useEffect(() => {
    if (realtimeData) {
      setLastUpdate(new Date().toISOString());
    }
  }, [realtimeData]);

  const handleManualRefresh = async () => {
    try {
      await refetch();
      toast({
        title: "Actualizado",
        description: "Datos actualizados correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron actualizar los datos",
        variant: "destructive",
      });
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Ahora mismo";
    if (diffMins === 1) return "Hace 1 minuto";
    if (diffMins < 60) return `Hace ${diffMins} minutos`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return "Hace 1 hora";
    return `Hace ${diffHours} horas`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);

    // Formatear hora en UTC con formato 24h
    const result = date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC", // ← MOSTRAR HORA UTC
    });

    return result;
  };

  const getChangeColorClass = (percent: number) => {
    if (percent >= 0) return "text-green-600";
    return "text-red-600";
  };

  if (!owner) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No hay información del dueño</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-medium">Error al cargar datos</p>
        <p className="text-muted-foreground text-center">
          No se pudieron cargar las estadísticas en tiempo real. Intenta
          recargar la página.
        </p>
        <Button onClick={handleManualRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Reintentar
        </Button>
      </div>
    );
  }

  const stats = realtimeData?.data;
  const lastUpdatedTime = formatTimeAgo(lastUpdate);

  return (
    <div className="space-y-6">
      {/* Header con título y botón de actualizar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Dashboard en Tiempo Real
          </h1>
          <p className="text-muted-foreground">
            Monitorea el rendimiento de todas tus tiendas • Moneda:{" "}
            {getCurrencyName(owner?.currency || "CUP")}
            {!isLoading && stats && (
              <span className="ml-2 inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span className="text-sm">Actualizado {lastUpdatedTime}</span>
              </span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleManualRefresh}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
          />
          Actualizar
        </Button>
      </div>

      {/* Tarjetas de métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Ventas Hoy */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ventas Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(
                      stats?.summary.totalRevenueToday || 0,
                      owner?.currency || "CUP",
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {stats?.summary.percentChange !== undefined && (
                      <>
                        {stats.summary.percentChange >= 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        <span
                          className={`text-sm font-medium ${getChangeColorClass(stats.summary.percentChange)}`}
                        >
                          {stats.summary.percentChange >= 0 ? "+" : ""}
                          {stats.summary.percentChange.toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                          vs ayer
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <DollarSign className="h-8 w-8 text-primary" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transacciones Hoy */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Transacciones Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">
                    {stats?.summary.totalSalesToday || 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Ticket promedio:{" "}
                    {formatCurrency(
                      stats?.summary.averageTicketToday || 0,
                      owner?.currency || "CUP",
                    )}
                  </div>
                </div>
                <ShoppingCart className="h-8 w-8 text-primary" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertas Activas */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Alertas Activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">
                    {(stats?.alerts?.lowStockCount || 0) +
                      (stats?.alerts?.noSalesTodayCount || 0)}
                  </div>
                  <div className="space-y-1 mt-1">
                    {stats?.alerts?.lowStockCount &&
                    stats.alerts.lowStockCount > 0 ? (
                      <div className="flex items-center gap-1 text-sm">
                        <AlertCircle className="h-3 w-3 text-amber-500" />
                        <span>{stats.alerts.lowStockCount} stock bajo</span>
                      </div>
                    ) : null}
                    {stats?.alerts?.noSalesTodayCount &&
                    stats.alerts.noSalesTodayCount > 0 ? (
                      <div className="flex items-center gap-1 text-sm">
                        <Store className="h-3 w-3 text-blue-500" />
                        <span>
                          {stats.alerts.noSalesTodayCount} tiendas sin ventas
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
                <AlertCircle className="h-8 w-8 text-amber-500" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tienda Top */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tienda Top Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-full" />
            ) : stats?.topShops && stats.topShops.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium">
                    {stats.topShops[0].shopName}
                  </div>
                  <Badge variant="secondary" className="ml-2">
                    #{1}
                  </Badge>
                </div>
                <div className="text-2xl font-bold">
                  {formatCurrency(
                    stats.topShops[0].revenueToday,
                    owner?.currency || "CUP",
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stats.topShops[0].salesCountToday} ventas
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-medium">Sin ventas hoy</div>
                  <div className="text-sm text-muted-foreground">
                    Todavía no hay actividad
                  </div>
                </div>
                <Store className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Card de discrepancias de caja */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Discrepancias de Caja Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {discrepanciesLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : discrepancies.length === 0 ? (
            <p className="text-muted-foreground">
              No hay discrepancias recientes en ninguna tienda
            </p>
          ) : (
            <div className="space-y-3">
              {discrepancies.slice(0, 3).map((d) => (
                <div
                  key={d.id}
                  className={`p-3 rounded border ${
                    d.discrepancyType === "faltante"
                      ? "bg-red-50 border-red-200"
                      : "bg-yellow-50 border-yellow-200"
                  }`}
                >
                  <div className="flex justify-between">
                    <div>
                      <div className="font-medium">
                        {d.shopName} •{" "}
                        {new Date(d.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {d.closedBy} • {d.cashRegisterName}
                      </div>
                    </div>
                    <div
                      className={`font-bold ${
                        d.discrepancyType === "faltante"
                          ? "text-red-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {d.discrepancyType === "faltante" ? "-" : "+"}
                      {formatCurrency(
                        Math.abs(parseFloat(d.cashDifference)),
                        owner?.currency || "CUP",
                      )}
                    </div>
                  </div>
                  {d.notes && (
                    <div className="mt-2 text-sm bg-white/50 p-2 rounded">
                      {d.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button variant="outline" size="sm" className="w-full" asChild>
            <Link href="/owner/discrepancies">Ver todas las discrepancias</Link>
          </Button>
        </CardFooter>
      </Card>

      {/* Segunda fila: Gráfico de tendencia y últimas ventas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tendencia por hora */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Tendencia Hoy por Hora</CardTitle>
            <CardDescription>
              Ventas distribuidas por hora del día
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : stats?.hourlyTrend ? (
              <div className="space-y-3">
                {stats.hourlyTrend
                  .filter((hour) => hour.salesCount > 0 || hour.revenue > 0)
                  .map((hour) => {
                    const maxRevenue = Math.max(
                      ...(stats.hourlyTrend.map((h) => h.revenue) || [0]),
                    );
                    const barWidth =
                      maxRevenue > 0 ? (hour.revenue / maxRevenue) * 100 : 0;

                    return (
                      <div key={hour.hour} className="flex items-center gap-3">
                        <div className="w-12 text-sm font-medium">
                          {hour.hour.toString().padStart(2, "0")}:00 UTC
                        </div>
                        <div className="flex-1">
                          <div
                            className="h-8 bg-primary/20 rounded-md transition-all duration-300"
                            style={{ width: `${Math.max(barWidth, 5)}%` }}
                          >
                            <div className="h-full bg-primary rounded-md"></div>
                          </div>
                        </div>
                        <div className="w-20 text-right">
                          <div className="font-medium">
                            {hour.salesCount} ventas
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatCurrency(
                              hour.revenue,
                              owner?.currency || "CUP",
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {(!stats.hourlyTrend.some((h) => h.salesCount > 0) ||
                  stats.hourlyTrend.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay ventas registradas hoy
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No hay datos de tendencia disponibles
              </div>
            )}
          </CardContent>
        </Card>

        {/* Últimas ventas */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Últimas Ventas</CardTitle>
            <CardDescription>
              Transacciones más recientes de todas las tiendas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : stats?.recentSales && stats.recentSales.length > 0 ? (
              <div className="space-y-3">
                {stats.recentSales.map((sale, index) => {
                  return (
                    <div
                      key={`${sale.saleId}-${index}`}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-full ${
                            sale.paymentMethod === "efectivo"
                              ? "bg-green-100 text-green-600"
                              : "bg-blue-100 text-blue-600"
                          }`}
                        >
                          {sale.paymentMethod === "efectivo" ? (
                            <DollarSign className="h-4 w-4" />
                          ) : (
                            <TrendingUp className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{sale.shopName}</div>
                          <div className="text-sm text-muted-foreground">
                            {sale.customerName || "Cliente ocasional"} •{" "}
                            {formatTime(sale.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          {formatCurrency(sale.total, owner?.currency || "CUP")}
                        </div>
                        <div className="text-xs capitalize text-muted-foreground">
                          {sale.paymentMethod}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No hay ventas registradas hoy
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tercera fila: Productos más vendidos y top tiendas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Productos más vendidos hoy */}
        <Card>
          <CardHeader>
            <CardTitle>Productos más Vendidos Hoy</CardTitle>
            <CardDescription>Los productos con más movimiento</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : stats?.topProductsToday && stats.topProductsToday.length > 0 ? (
              <div className="space-y-3">
                {stats.topProductsToday.map((product, index) => (
                  <div
                    key={product.productId}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-primary/10 text-primary">
                        <Package className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-medium">{product.productName}</div>
                        <div className="text-sm text-muted-foreground capitalize">
                          {product.category} • {product.totalSold} unidades
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">
                        {formatCurrency(
                          product.totalRevenue,
                          owner?.currency || "CUP",
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {product.totalSold} vendidos
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No hay productos vendidos hoy
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Tiendas */}
        <Card>
          <CardHeader>
            <CardTitle>Top Tiendas Hoy</CardTitle>
            <CardDescription>Rendimiento por tienda</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : stats?.topShops && stats.topShops.length > 0 ? (
              <div className="space-y-3">
                {stats.topShops.map((shop, index) => (
                  <div
                    key={shop.shopId}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={index === 0 ? "default" : "secondary"}
                        className="ml-2"
                      >
                        #{index + 1}
                      </Badge>
                      <div>
                        <div className="font-medium">{shop.shopName}</div>
                        <div className="text-sm text-muted-foreground">
                          {shop.salesCountToday} ventas
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">
                        {formatCurrency(
                          shop.revenueToday,
                          owner?.currency || "CUP",
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Ticket:{" "}
                        {formatCurrency(
                          shop.revenueToday / (shop.salesCountToday || 1),
                          owner?.currency || "CUP",
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No hay actividad en las tiendas hoy
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer info */}
      <div className="text-center text-sm text-muted-foreground pt-4 border-t">
        <p>
          Los datos se actualizan automáticamente cada 60 segundos. Puedes
          forzar una actualización con el botón "Actualizar".
        </p>
        <p className="mt-1">
          Mostrando datos del día{" "}
          {new Date().toLocaleDateString("es-CU", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      </div>
    </div>
  );
}
